// @flow

import * as React from 'react';

import clsx from 'clsx';

import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import type { Location } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { selectCountItems } from '@gandi/cart';
import { NotificationsPlaceholder } from '@gandi/notifications';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import Footer from '../../../components/Footer';
import { selectIsCheckoutPage } from '../../../components/modules/selectors';
import type { FullState, PreFetchArgs, PreFetchReturn } from '../../../types';

import type { BreadCrumbProps } from '../../components/Breadcrumb/Breadcrumb';
import { CartSummary } from '../../components/CartSummary';
import { FunnelControls } from '../../components/FunnelControls/FunnelControls';
import { FunnelWidgets } from '../../components/FunnelWidgets/FunnelWidgets';
import { Header } from '../../components/Header';
import { LandingPagesControls } from '../../components/LandingPagesControls/LandingPagesControls';
import { LandingPagesWidgets } from '../../components/LandingPagesWidgets/LandingPagesWidgets';
import { Main } from '../../components/Main';
import { Navigation } from '../../components/Navigation';
import PreferencesWidget from '../../components/PreferencesWidget/PreferencesWidget';
import type { DialogState } from '../../components/PreferencesWidget/types';
import { SkipLinks } from '../../components/SkipLinks';
import { CartSummaryProvider } from '../../context/CartSummary';
import { NavigationContext } from '../../context/Navigation';
import { useNavigationState, useOnClickOutside } from '../../hooks';

import styles from './ShopLayout.scss';

export type Props = {
  backgroundColor?: 'surface',
  showCartSummary?: boolean,
  widgets?: React.Node,
  children?: React.Node,
  dashboardURL: string,
  preferencesDialog: DialogState,
  currentStep?: BreadCrumbProps['currentStep'],
};

const smallContainerPage = [
  'account-choice',
  'configure',
  'account',
  'mailboxes',
  'overview',
  'checkout',
  'payment-return',
];

function isSmall(location: Location) {
  return smallContainerPage.some(page => location.pathname.includes(page));
}

function ShopLayout({
  showCartSummary,
  currentStep,
  ...props
}: Props): React.MixedElement {
  const navigate = useNavigate();
  const [navigation, toggleOpen] = useNavigationState();
  const navigationRef = React.useRef<HTMLElement | null>(null);

  const location = useLocation();
  const showFunnelWidgets = selectIsCheckoutPage(location);

  const countItems = useAppSelector(selectCountItems);

  const hasProducts = countItems > 0;

  const outletContext = useOutletContext();

  React.useEffect(() => {
    // User deleted all cart products, redirect to empty cart screen.
    if (countItems === 0 && currentStep === 1) {
      navigate('/cart', { replace: true });
    }
  }, [countItems, navigate, currentStep]);

  /**
   * Handle click events outside of the Navigation.
   * Should close it on mobile.
   */
  const handleClickOutside = React.useCallback(() => {
    // $FlowFixMe[prop-missing]
    if (navigation.open) toggleOpen();
    // $FlowFixMe[prop-missing]
  }, [navigation.open, toggleOpen]);

  useOnClickOutside(navigationRef, handleClickOutside);

  return (
    <Base
      backgroundColor={
        props.backgroundColor || outletContext?.backgroundColor || 'background'
      }
      className={clsx(styles.root, {
        // $FlowFixMe[prop-missing]
        [styles.isOpen]: navigation.open,
      })}
    >
      <SkipLinks />

      <NotificationsPlaceholder className={styles.notifications} />

      <Header
        className={clsx(
          styles.header,
          showFunnelWidgets ? styles.funnelHeader : styles.landingPagesHeader,
        )}
        controls={
          showFunnelWidgets && hasProducts ? (
            <FunnelControls currentStep={currentStep} />
          ) : (
            <LandingPagesControls
              hamburgerClassName={styles.hamburger}
              toggleOpen={toggleOpen}
            />
          )
        }
        widgets={
          showFunnelWidgets ? (
            <FunnelWidgets preferencesDialog={props.preferencesDialog} />
          ) : (
            <LandingPagesWidgets
              preferencesDialog={props.preferencesDialog}
              widgets={props.widgets}
            />
          )
        }
      />

      {/* $FlowFixMe[incompatible-exact] */}
      <NavigationContext.Provider value={navigation}>
        <Navigation innerRef={navigationRef} className={styles.navigation} />
      </NavigationContext.Provider>

      <CartSummaryProvider>
        <>
          <Base
            className={showCartSummary ? styles.cart2ColumnWrapper : undefined}
          >
            <Base
              className={clsx(
                styles.mainWrapper,
                isSmall(location) && !showCartSummary
                  ? styles.mainWrapperSmall
                  : undefined,
                showCartSummary ? styles.mainWrapperCart2Columns : undefined,
              )}
            >
              <Main className={styles.main}>{props.children}</Main>
            </Base>
            {showCartSummary && <CartSummary />}
          </Base>
          <Footer showCart2Column={showCartSummary} />
        </>
      </CartSummaryProvider>
    </Base>
  );
}

ShopLayout.preFetch = function preFetch(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  return PreferencesWidget.preFetch(args);
};

ShopLayout.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  return PreferencesWidget.handleRedirect(args);
};

export { ShopLayout };
